/* global axios */
import ApiClient from './ApiClient';

/**
 * API da tela do Correio (docs/email/TELA.md). O servidor decide quem vê o
 * quê (caixa pessoal só a dona, nem administrador); aqui só se pergunta.
 */
class CorreioAPI extends ApiClient {
  constructor() {
    super('correio', { accountScoped: true });
  }

  estado() {
    return axios.get(`${this.url}/estado`);
  }

  /** A bolinha vermelha do menu: não lidas na Entrada das caixas que a pessoa vê. */
  naoLidas() {
    return axios.get(`${this.url}/nao_lidas`);
  }

  entrar({ email, senha }) {
    return axios.post(`${this.url}/entrar`, { email, senha });
  }

  desconectar(contaId) {
    return axios.delete(`${this.url}/caixas/${contaId}`);
  }

  pastas(contaId) {
    return axios.get(`${this.url}/pastas`, { params: { conta_id: contaId } });
  }

  /** Uma pasta (`pastaId`) ou uma vista da caixa: `categoriaId`, `vista: 'adiados'`. */
  mensagens({ contaId, pastaId, categoriaId, vista, busca, pagina }) {
    return axios.get(`${this.url}/mensagens`, {
      params: {
        conta_id: contaId,
        pasta_id: pastaId,
        categoria_id: categoriaId,
        vista,
        busca,
        pagina,
      },
    });
  }

  mensagem(id) {
    return axios.get(`${this.url}/mensagens/${id}`);
  }

  /** extra: { destinoPastaId, ate, categoriaIds } conforme a ação. */
  agir(id, acao, extra = {}) {
    return axios.patch(`${this.url}/mensagens/${id}`, {
      acao,
      destino_pasta_id: extra.destinoPastaId,
      ate: extra.ate,
      categoria_ids: extra.categoriaIds,
    });
  }

  /** "Exibir imagens": o servidor busca e devolve { url: 'data:…' | null }. */
  exibirImagens(mensagemId) {
    return axios.post(`${this.url}/mensagens/${mensagemId}/imagens`);
  }

  categorias(contaId) {
    return axios.get(`${this.url}/categorias`, {
      params: { conta_id: contaId },
    });
  }

  salvarCategoria(contaId, { id, ...dados }) {
    const corpo = { conta_id: contaId, ...dados };
    return id
      ? axios.patch(`${this.url}/categorias/${id}`, corpo)
      : axios.post(`${this.url}/categorias`, corpo);
  }

  apagarCategoria(contaId, id) {
    return axios.delete(`${this.url}/categorias/${id}`, {
      params: { conta_id: contaId },
    });
  }

  preferencias(contaId) {
    return axios.get(`${this.url}/preferencias`, {
      params: { conta_id: contaId },
    });
  }

  salvarPreferencias(contaId, dados) {
    return axios.patch(`${this.url}/preferencias`, {
      conta_id: contaId,
      ...dados,
    });
  }

  enviarImagemDaAssinatura(contaId, arquivo) {
    const fd = new FormData();
    fd.append('conta_id', contaId);
    fd.append('arquivo', arquivo);
    return axios.post(`${this.url}/preferencias/imagens`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  rascunhos(contaId) {
    return axios.get(`${this.url}/rascunhos`, {
      params: { conta_id: contaId },
    });
  }

  rascunho(contaId, id) {
    return axios.get(`${this.url}/rascunhos/${id}`, {
      params: { conta_id: contaId },
    });
  }

  salvarRascunho(contaId, id, dados) {
    const corpo = { conta_id: contaId, ...dados };
    return id
      ? axios.patch(`${this.url}/rascunhos/${id}`, corpo)
      : axios.post(`${this.url}/rascunhos`, corpo);
  }

  descartarRascunho(contaId, id) {
    return axios.delete(`${this.url}/rascunhos/${id}`, {
      params: { conta_id: contaId },
    });
  }

  urlDoAnexo(mensagemId, anexoId) {
    return `${this.url}/mensagens/${mensagemId}/anexos/${anexoId}`;
  }

  baixarAnexo(mensagemId, anexoId) {
    return axios.get(this.urlDoAnexo(mensagemId, anexoId), {
      responseType: 'blob',
    });
  }

  vincular(mensagemId, tipo, alvoId) {
    return axios.post(`${this.url}/mensagens/${mensagemId}/vinculos`, {
      tipo,
      alvo_id: alvoId,
    });
  }

  desvincular(mensagemId, vinculoId) {
    return axios.delete(
      `${this.url}/mensagens/${mensagemId}/vinculos/${vinculoId}`
    );
  }

  envios(contaId) {
    return axios.get(`${this.url}/envios`, { params: { conta_id: contaId } });
  }

  /** Com anexos vai como formulário (arquivo não viaja em JSON). */
  enviar({ contaId, anexos = [], ...campos }) {
    const dados = {
      conta_id: contaId,
      para: campos.para,
      cc: campos.cc,
      cco: campos.cco,
      assunto: campos.assunto,
      texto: campos.texto,
      html: campos.html,
      rastrear: campos.rastrear,
      em_resposta_a_id: campos.emRespostaAId,
      encaminhar_de_id: campos.encaminharDeId,
      agendar_para: campos.agendarPara,
      rascunho_id: campos.rascunhoId,
    };
    if (!anexos.length) return axios.post(`${this.url}/envios`, dados);

    const fd = new FormData();
    Object.entries(dados).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, v);
    });
    anexos.forEach(a => fd.append('anexos[]', a));
    return axios.post(`${this.url}/envios`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  desfazer(envioId) {
    return axios.delete(`${this.url}/envios/${envioId}`);
  }

  servidores() {
    return axios.get(`${this.url}/servidores`);
  }

  salvarServidor(servidor) {
    return servidor.id
      ? axios.patch(`${this.url}/servidores/${servidor.id}`, servidor)
      : axios.post(`${this.url}/servidores`, servidor);
  }

  apagarServidor(id) {
    return axios.delete(`${this.url}/servidores/${id}`);
  }

  testarServidor(servidor, email, senha) {
    return axios.post(`${this.url}/servidores/testar`, {
      ...servidor,
      email,
      senha,
    });
  }
}

export default new CorreioAPI();
